import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { useCancellationOverlay } from "@/components/cancellation-overlay-context";
import { OverlayCloseButton, OverlayPanel, overlayBackdropClassName } from "@/components/ui/overlay-shell";
import { submitCancellationForm, type CancellationFormState } from "@/app/actions";
import { Loader2, Send } from "lucide-react";

export function CancellationOverlay() {
  const { isOpen, closeCancellation } = useCancellationOverlay();

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") closeCancellation(); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, closeCancellation]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) closeCancellation(); }}
    >
      <div aria-hidden="true" className={`${overlayBackdropClassName} pointer-events-none`} />
      <OverlayPanel className="w-full h-auto max-h-full sm:max-h-[88vh] max-w-2xl flex flex-col">
        <div className="flex items-start justify-between gap-4 border-b border-white/10 px-5 pt-8 pb-5 shrink-0 md:px-6 md:pt-5">
          <div className="min-w-0">
            <h3 className="text-xl font-bold tracking-tight">Abo kündigen</h3>
            <p className="mt-1 text-sm text-white/60">Wir werden uns schnellstmöglich bei dir melden.</p>
          </div>
          <OverlayCloseButton onClick={closeCancellation} />
        </div>
        <div className="overflow-y-auto overscroll-contain p-5 md:p-6">
          <CancellationForm onSuccess={closeCancellation} />
        </div>
      </OverlayPanel>
    </div>
  );
}

function CancellationForm({ onSuccess }: { onSuccess?: () => void }) {
  const initialState: CancellationFormState = { success: false, message: "" };
  const [state, setState] = useState<CancellationFormState>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [memberNumber, setMemberNumber] = useState("");
  const [privacy, setPrivacy] = useState(false);

  const isFormValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    memberNumber.trim().length > 0 &&
    privacy;

  const handleInvalid = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const t = e.target as HTMLInputElement;
    if (t.validity.valueMissing) t.setCustomValidity("Bitte fülle dieses Feld aus.");
    else if (t.validity.typeMismatch && t.type === "email") t.setCustomValidity("Bitte eine gültige E-Mail-Adresse eingeben.");
    else t.setCustomValidity("");
  };

  const handleInput = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    (e.target as HTMLInputElement).setCustomValidity("");
  };

  useEffect(() => {
    if (!state.message) return;
    if (state.success) {
      toast({ title: "Kündigung übermittelt", description: state.message });
      formRef.current?.reset();
      setFirstName(""); setLastName(""); setMemberNumber(""); setPrivacy(false);
      onSuccess?.();
      return;
    }
    toast({ title: "Formular unvollständig", description: state.message, variant: "destructive" });
  }, [state, onSuccess]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData(e.currentTarget);
      const nextState = await submitCancellationForm(formData);
      setState(nextState);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="cancel-first-name" className="text-sm font-semibold text-foreground">Vorname</label>
          <Input
            id="cancel-first-name"
            name="firstName"
            autoComplete="given-name"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onInvalid={handleInvalid}
            onInput={handleInput}
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="cancel-last-name" className="text-sm font-semibold text-foreground">Nachname</label>
          <Input
            id="cancel-last-name"
            name="lastName"
            autoComplete="family-name"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onInvalid={handleInvalid}
            onInput={handleInput}
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <label htmlFor="cancel-member-number" className="text-sm font-semibold text-foreground">Mitgliedsnummer</label>
          <Input
            id="cancel-member-number"
            name="memberNumber"
            placeholder="z. B. 12345"
            required
            value={memberNumber}
            onChange={(e) => setMemberNumber(e.target.value)}
            onInvalid={handleInvalid}
            onInput={handleInput}
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="cancel-email" className="text-sm font-semibold text-foreground">
            E-Mail <span className="text-white/40 font-normal">(optional)</span>
          </label>
          <Input
            id="cancel-email"
            name="email"
            type="email"
            autoComplete="email"
            onInvalid={handleInvalid}
            onInput={handleInput}
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="cancel-phone" className="text-sm font-semibold text-foreground">
            Rückrufnummer <span className="text-white/40 font-normal">(optional)</span>
          </label>
          <Input
            id="cancel-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            placeholder="z. B. 06021 452138"
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <label htmlFor="cancel-reason" className="text-sm font-semibold text-foreground">
            Grund <span className="text-white/40 font-normal">(optional)</span>
          </label>
          <Textarea
            id="cancel-reason"
            name="reason"
            placeholder="Wenn du möchtest, kannst du uns hier kurz den Kündigungsgrund nennen."
            className="min-h-[80px]"
            onInvalid={handleInvalid}
            onInput={handleInput}
          />
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-background/40 px-3 py-3">
        <input
          id="cancel-privacy"
          name="privacy"
          type="checkbox"
          value="yes"
          required
          checked={privacy}
          onChange={(e) => setPrivacy(e.target.checked)}
          onInvalid={(e) => { (e.target as HTMLInputElement).setCustomValidity("Bitte stimme den Datenschutzbestimmungen zu."); }}
          onInput={(e) => { (e.target as HTMLInputElement).setCustomValidity(""); }}
          className="mt-0.5 h-5 w-5 min-w-[1.25rem] rounded border border-white/20 bg-background/60 checked:bg-white checked:border-white accent-white cursor-pointer"
        />
        <label htmlFor="cancel-privacy" className="text-sm text-foreground/80 leading-snug">
          Ich stimme zu, dass meine Angaben zur Bearbeitung meiner Kündigung gespeichert und verarbeitet werden.
        </label>
      </div>

      <Button
        type="submit"
        className="w-full transition-opacity duration-200"
        disabled={isSubmitting || !isFormValid}
      >
        {isSubmitting ? (
          <span className="inline-flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Senden...
          </span>
        ) : (
          <span className="inline-flex items-center justify-center gap-2">
            <Send className="h-4 w-4" />
            Kündigung übermitteln
          </span>
        )}
      </Button>
    </form>
  );
}
